import { useRef, useState, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Share2, Check } from 'lucide-react';
import { C } from '../constants';

/* ── Design tokens (matching reference) ───────────────────────────────────── */
const T = {
  bgDeep: '#060611',
  bgCard: '#0d0d1f',
  border: '#1a1a3e',
  cyan:   '#00d4ff',
  pink:   '#ff2d78',
  green:  '#22c55e',
  text:   '#f0f0f5',
  textS:  '#8a8aa3',
  textM:  '#5a5a73',
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */
function pct(count, total) {
  return total > 0 ? (count / total * 100) : 0;
}

function BarRow({ label, pctVal, color, height = 24, fontSize = 11, labelWidth = 60 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: labelWidth, fontSize, fontWeight: 600, textAlign: 'right', color: T.textS, fontFamily: 'Arial, sans-serif' }}>
        {label}
      </div>
      <div style={{ flex: 1, height, background: 'rgba(255,255,255,0.04)', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{
          width: `${Math.max(pctVal, 2)}%`,
          height: '100%',
          borderRadius: 6,
          background: color,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: 8,
          fontSize: fontSize - 1,
          fontWeight: 700,
          color: 'white',
          whiteSpace: 'nowrap',
          fontFamily: 'Arial, sans-serif',
          minWidth: pctVal > 8 ? undefined : 0,
        }}>
          {pctVal > 8 ? `${pctVal.toFixed(1)}%` : ''}
        </div>
      </div>
      {pctVal <= 8 && (
        <div style={{ fontSize: fontSize - 1, fontWeight: 700, color: T.textS, width: 36, fontFamily: 'Arial, sans-serif' }}>
          {pctVal.toFixed(0)}%
        </div>
      )}
    </div>
  );
}

/* ── Shared logo block used by all cards ─────────────────────────────────── */
function LogoBadge({ size = 28, fontSize = 13 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <img
        src="/ue-logo.jpg"
        alt="UE"
        crossOrigin="anonymous"
        style={{ width: size, height: size, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
      />
      <div style={{ fontFamily: 'Arial, sans-serif', fontSize, fontWeight: 700, lineHeight: 1.1 }}>
        <span style={{ color: T.text }}>Unofficial</span>
        <span style={{ color: '#F97316' }}>Election</span>
      </div>
    </div>
  );
}

/* ── SQUARE CARD (1:1) ────────────────────────────────────────────────────── */
const SquareCard = forwardRef(function SquareCard({ stateName, stateId, leaderParty, leaderPct, totalVotes, numParties, topParties }, ref) {
  return (
    <div ref={ref} style={{
      width: 400, height: 400,
      background: T.bgDeep,
      borderRadius: 20,
      overflow: 'hidden',
      position: 'relative',
      padding: 28,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      fontFamily: 'Arial, sans-serif',
    }}>
      {/* Glow */}
      <div style={{ position: 'absolute', top: '-40%', right: '-20%', width: 300, height: 300, background: `radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Top row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
        <LogoBadge size={28} fontSize={13} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 9, textTransform: 'uppercase', letterSpacing: '1.5px', color: T.green, fontWeight: 600 }}>
          <div style={{ width: 6, height: 6, background: T.green, borderRadius: '50%' }} />
          Live
        </div>
      </div>

      {/* Middle */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '2px', color: T.cyan, fontWeight: 600, marginBottom: 4 }}>
          {stateName} 2026
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2, marginBottom: 16, color: T.text }}>
          <span style={{ color: leaderParty?.color || T.cyan }}>
            {leaderParty?.en || '—'}
          </span>
          <span> leads statewide</span>
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.cyan }}>{leaderPct}%</div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '1.5px', color: T.textM, marginTop: 2 }}>{leaderParty?.en} Vote Share</div>
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.text }}>{totalVotes.toLocaleString()}</div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '1.5px', color: T.textM, marginTop: 2 }}>Total Votes</div>
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 800, color: T.text }}>{numParties}</div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '1.5px', color: T.textM, marginTop: 2 }}>Parties</div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
        <div style={{ fontSize: 12, color: T.textS }}>
          <span style={{ color: T.text, fontWeight: 700 }}>Vote now</span> — it takes 10 seconds
        </div>
        <div style={{ fontSize: 10, color: T.textM }}>unofficialelections.com</div>
      </div>
    </div>
  );
});

/* ── STORY CARD (9:16) ────────────────────────────────────────────────────── */
const StoryCard = forwardRef(function StoryCard({ stateName, stateId, district, districtLeader, districtTopParties, districtTotal }, ref) {
  const locationLabel = district || stateName;
  return (
    <div ref={ref} style={{
      width: 360, height: 640,
      background: T.bgDeep,
      borderRadius: 20,
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Arial, sans-serif',
    }}>
      {/* Glows */}
      <div style={{ position: 'absolute', top: '-50%', right: '-30%', width: 400, height: 400, background: `radial-gradient(circle, rgba(0,212,255,0.08) 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-30%', left: '-20%', width: 350, height: 350, background: `radial-gradient(circle, rgba(255,45,120,0.06) 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Header */}
      <div style={{ padding: '28px 24px 16px', position: 'relative', zIndex: 1 }}>
        <LogoBadge size={30} fontSize={14} />
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 100, padding: '4px 10px', fontSize: 9, color: T.textS, marginTop: 10 }}>
          <div style={{ width: 5, height: 5, background: T.green, borderRadius: '50%' }} />
          Live results · 100% anonymous
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, padding: '0 24px', position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.1, marginBottom: 4, color: T.text }}>
          {locationLabel}
        </div>
        <div style={{ fontSize: 13, color: T.cyan, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 24 }}>
          {stateName} 2026
        </div>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 2, color: T.textM, marginBottom: 8 }}>
          Leading party
        </div>
        <div style={{ fontSize: 42, fontWeight: 800, color: districtLeader?.party?.color || T.cyan, marginBottom: 24 }}>
          {districtLeader?.party?.en || '—'}
        </div>

        {/* Bars */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {districtTopParties.map(({ party, pct: pctVal }) => party && (
            <BarRow key={party.id} label={party.en} pctVal={pctVal} color={party.color} height={24} fontSize={12} labelWidth={60} />
          ))}
        </div>

        <div style={{ marginTop: 20, fontSize: 12, color: T.textM }}>
          <strong style={{ color: T.text, fontSize: 18 }}>{districtTotal.toLocaleString()}</strong> votes in {district ? 'this district' : 'total'}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '20px 24px 28px', position: 'relative', zIndex: 1 }}>
        <div style={{ background: `linear-gradient(135deg, ${T.cyan}, ${T.pink})`, borderRadius: 12, padding: '14px 20px', textAlign: 'center', fontWeight: 700, fontSize: 14, color: 'white' }}>
          Cast your vote now
        </div>
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11, color: T.textM }}>
          unofficialelections.com
        </div>
      </div>
    </div>
  );
});

/* ── WIDE CARD (2:1) ──────────────────────────────────────────────────────── */
const WideCard = forwardRef(function WideCard({ stateName, stateId, district, districtTopParties }, ref) {
  const locationLabel = district || stateName;
  return (
    <div ref={ref} style={{
      width: 600, height: 314,
      background: T.bgDeep,
      borderRadius: 16,
      overflow: 'hidden',
      position: 'relative',
      display: 'flex',
      fontFamily: 'Arial, sans-serif',
    }}>
      {/* Glow */}
      <div style={{ position: 'absolute', top: '-50%', left: '-20%', width: 400, height: 400, background: `radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)`, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '-40%', right: '-10%', width: 300, height: 300, background: `radial-gradient(circle, rgba(255,45,120,0.05) 0%, transparent 70%)`, pointerEvents: 'none' }} />

      {/* Left */}
      <div style={{ flex: 1, padding: 28, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
        <LogoBadge size={26} fontSize={12} />
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1.1, color: T.text }}>{locationLabel}</div>
          <div style={{ fontSize: 12, color: T.cyan, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 2, marginTop: 4 }}>{stateName} 2026</div>
        </div>
        <div style={{ fontSize: 10, color: T.textM }}>unofficialelections.com · Free anonymous poll</div>
      </div>

      {/* Right — bars */}
      <div style={{ width: 220, padding: '28px 28px 28px 0', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8, position: 'relative', zIndex: 1 }}>
        {districtTopParties.map(({ party, pct: pctVal }) => party && (
          <BarRow key={party.id} label={party.en} pctVal={pctVal} color={party.color} height={20} fontSize={11} labelWidth={50} />
        ))}
      </div>
    </div>
  );
});

/* ── MODAL ────────────────────────────────────────────────────────────────── */
export default function ShareCardModal({ stateData, counts, userVote, onClose }) {
  const [format, setFormat]   = useState('square');
  const [status, setStatus]   = useState('idle'); // idle | loading | done
  const cardRef = useRef(null);

  const { partyById, parties, name: stateName, id: stateId } = stateData;

  /* Statewide aggregation */
  const statewideByParty = {};
  Object.values(counts).forEach(dc =>
    Object.entries(dc).forEach(([pid, c]) => {
      statewideByParty[pid] = (statewideByParty[pid] || 0) + c;
    })
  );
  const totalVotes = Object.values(statewideByParty).reduce((a, b) => a + b, 0);

  let leaderId = null, leaderCount = 0;
  Object.entries(statewideByParty).forEach(([pid, c]) => {
    if (c > leaderCount) { leaderId = pid; leaderCount = c; }
  });
  const leaderParty = leaderId ? partyById[leaderId] : null;
  const leaderPct   = totalVotes > 0 ? (leaderCount / totalVotes * 100).toFixed(1) : '0.0';

  /* District data (for story / wide) */
  const district      = userVote?.district || null;
  const districtCnts  = district ? (counts[district] || {}) : statewideByParty;
  const districtTotal = Object.values(districtCnts).reduce((a, b) => a + b, 0);
  const districtTopParties = Object.entries(districtCnts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([pid, c]) => ({ party: partyById[pid], pct: pct(c, districtTotal) }));
  const districtLeader = districtTopParties[0] || null;

  /* Capture scale per format */
  const captureScale = format === 'story' ? 3 : format === 'wide' ? 2 : 2.7;

  /* Capture */
  const capture = async () => {
    const { default: html2canvas } = await import('html2canvas');
    return html2canvas(cardRef.current, {
      scale: captureScale,
      useCORS: true,
      backgroundColor: T.bgDeep,
      logging: false,
    });
  };

  const handleDownload = async () => {
    setStatus('loading');
    try {
      const canvas = await capture();
      const a = document.createElement('a');
      a.download = `unofficialelection-${stateId}-${format}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
      setStatus('done');
      setTimeout(() => setStatus('idle'), 2500);
    } catch { setStatus('idle'); }
  };

  const handleShare = async () => {
    setStatus('loading');
    try {
      const canvas = await capture();
      canvas.toBlob(async (blob) => {
        const file = new File([blob], `results-${stateId}.png`, { type: 'image/png' });
        if (navigator.share && navigator.canShare?.({ files: [file] })) {
          await navigator.share({
            files:  [file],
            title:  `${stateName} Election Results`,
            text:   `See how ${stateName} is voting! Cast your anonymous vote at unofficialelections.com`,
          });
        } else {
          // Fallback: download
          const a = document.createElement('a');
          a.download = `results-${stateId}.png`;
          a.href = canvas.toDataURL('image/png');
          a.click();
        }
        setStatus('idle');
      });
    } catch { setStatus('idle'); }
  };

  const formats = [
    { id: 'square', label: 'Square',  sub: '1 : 1'  },
    { id: 'story',  label: 'Story',   sub: '9 : 16' },
    { id: 'wide',   label: 'Wide',    sub: '2 : 1'  },
  ];

  /* Preview display sizes — kept small so the dialog always fits on screen */
  const previewMeta = {
    square: { w: 400, h: 400, scale: 0.62 },
    story:  { w: 360, h: 640, scale: 0.38 },
    wide:   { w: 600, h: 314, scale: 0.62 },
  }[format];

  const displayW = Math.round(previewMeta.w * previewMeta.scale);
  const displayH = Math.round(previewMeta.h * previewMeta.scale);

  const sharedCardProps = {
    stateName, stateId, leaderParty, leaderPct, totalVotes,
    numParties: parties.length, district, districtLeader,
    districtTopParties, districtTotal,
  };

  const exportNote = {
    square: '~1080 × 1080 px · Instagram post',
    story:  '~1080 × 1920 px · Instagram / WhatsApp story',
    wide:   '~1200 × 628 px · WhatsApp / Twitter',
  }[format];

  const modal = (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
        background: 'rgba(6,6,17,0.90)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Hidden full-size card for html2canvas — positioned off-screen */}
      <div style={{ position: 'fixed', left: -9999, top: 0, pointerEvents: 'none' }}>
        {format === 'square' && <SquareCard ref={cardRef} {...sharedCardProps} />}
        {format === 'story'  && <StoryCard  ref={cardRef} {...sharedCardProps} />}
        {format === 'wide'   && <WideCard   ref={cardRef} {...sharedCardProps} />}
      </div>

      {/* Dialog — flex column so buttons are always pinned at bottom */}
      <div style={{
        background: '#0d0d1f',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 20,
        width: '100%',
        maxWidth: 480,
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* ── Scrollable content ─────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.4rem 1.4rem 0' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.2rem' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '1.05rem', color: C.white }}>Share Results</div>
              <div style={{ fontSize: '0.76rem', color: C.textDim, marginTop: 2 }}>
                Download a card and share on WhatsApp or Instagram
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textDim, padding: '2px 2px 0', display: 'flex', flexShrink: 0 }}>
              <X size={18} />
            </button>
          </div>

          {/* Format tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: '1.2rem' }}>
            {formats.map(f => (
              <button
                key={f.id}
                onClick={() => setFormat(f.id)}
                style={{
                  flex: 1, padding: '7px 6px', borderRadius: 10,
                  border: `1px solid ${format === f.id ? C.cyan + '55' : 'rgba(255,255,255,0.08)'}`,
                  background: format === f.id ? 'rgba(0,229,255,0.08)' : 'transparent',
                  color: format === f.id ? C.cyan : C.textDim,
                  fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                  fontFamily: "'Poppins', sans-serif", transition: 'all 150ms ease',
                }}
              >
                <div>{f.label}</div>
                <div style={{ fontSize: '0.62rem', opacity: 0.5, marginTop: 1 }}>{f.sub}</div>
              </button>
            ))}
          </div>

          {/* Preview */}
          <div style={{
            width: displayW,
            height: displayH,
            overflow: 'hidden',
            margin: '0 auto 0.75rem',
            borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <div style={{
              transform: `scale(${previewMeta.scale})`,
              transformOrigin: 'top left',
              width: previewMeta.w,
              height: previewMeta.h,
            }}>
              {format === 'square' && <SquareCard {...sharedCardProps} />}
              {format === 'story'  && <StoryCard  {...sharedCardProps} />}
              {format === 'wide'   && <WideCard   {...sharedCardProps} />}
            </div>
          </div>

          {/* Export size note */}
          <div style={{ textAlign: 'center', fontSize: '0.7rem', color: C.textDim, opacity: 0.6, paddingBottom: '1.2rem' }}>
            {exportNote}
          </div>
        </div>

        {/* ── Sticky action buttons ──────────────────────────────────────── */}
        <div style={{
          padding: '1rem 1.4rem 1.4rem',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          gap: 10,
          flexShrink: 0,
        }}>
          <button
            onClick={handleDownload}
            disabled={status === 'loading'}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '11px 16px', borderRadius: 12,
              background: status === 'done'
                ? 'rgba(34,197,94,0.15)'
                : `linear-gradient(135deg, ${C.cyan}, ${C.pink})`,
              border: status === 'done' ? '1px solid rgba(34,197,94,0.4)' : 'none',
              color: status === 'done' ? '#22c55e' : C.white,
              fontWeight: 700, fontSize: '0.85rem',
              cursor: status === 'loading' ? 'not-allowed' : 'pointer',
              opacity: status === 'loading' ? 0.7 : 1,
              fontFamily: "'Poppins', sans-serif",
              transition: 'all 200ms ease',
            }}
          >
            {status === 'done' ? <Check size={15} /> : <Download size={15} />}
            {status === 'loading' ? 'Generating…' : status === 'done' ? 'Saved!' : 'Download PNG'}
          </button>

          <button
            onClick={handleShare}
            disabled={status === 'loading'}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '11px 18px', borderRadius: 12,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: C.white, fontWeight: 600, fontSize: '0.85rem',
              cursor: status === 'loading' ? 'not-allowed' : 'pointer',
              opacity: status === 'loading' ? 0.7 : 1,
              fontFamily: "'Poppins', sans-serif",
              flexShrink: 0,
            }}
          >
            <Share2 size={15} />
            Share
          </button>
        </div>

      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
